import type { MessageEnvelope } from "@pubnub/react-chat-components";

import type { InboxChat } from "context/ChatUnreadContext";

const SHIPMENT_BOOKED_CHANNEL_PREFIX = "shipment.booked.";

export function getShipmentIdFromChannelNameOrNull(
  channelName: string,
): number | null {
  if (!channelName.startsWith(SHIPMENT_BOOKED_CHANNEL_PREFIX)) return null;
  const suffix = channelName.slice(SHIPMENT_BOOKED_CHANNEL_PREFIX.length);
  const parsed = Number(suffix);
  if (!Number.isInteger(parsed)) return null;
  return parsed;
}

interface MessagePayloadLike {
  text?: unknown;
  sender?: { name?: unknown };
  meta?: { is_file?: unknown };
  file?: unknown;
}

export function extractSenderName(payload: MessagePayloadLike): string {
  const { sender } = payload;
  if (sender !== null && typeof sender === "object" && "name" in sender) {
    const { name } = sender;
    if (typeof name === "string" && name.length > 0) return name;
  }
  return "Someone";
}

export function payloadIsAttachment(payload: MessagePayloadLike): boolean {
  if ("file" in payload && payload.file !== undefined) return true;
  const { meta } = payload;
  if (meta !== null && typeof meta === "object" && "is_file" in meta) {
    return Boolean(meta.is_file);
  }
  return false;
}

export function buildPreviewText(
  payload: MessagePayloadLike,
  senderName: string,
): string {
  if (payloadIsAttachment(payload)) return `${senderName} sent an attachment`;
  if (typeof payload.text === "string") return payload.text;
  return "";
}

/**
 * Builds an InboxChat row from an incoming PubNub message envelope. Returns
 * null if the channel isn't a known shipment chat (defensive — the realtime
 * listener subscribes to the user's channel group which today only contains
 * `shipment.booked.{id}` channels).
 */
export function inboxChatFromMessageEnvelopeOrNull(
  envelope: MessageEnvelope,
): InboxChat | null {
  if (envelope.channel === undefined || envelope.timetoken === undefined)
    return null;
  const shipmentId = getShipmentIdFromChannelNameOrNull(envelope.channel);
  if (shipmentId === null) return null;

  const payload = (envelope.message ?? {}) as MessagePayloadLike;
  const senderName = extractSenderName(payload);
  const preview = buildPreviewText(payload, senderName);

  return {
    channel_name: envelope.channel,
    shipment_id: shipmentId,
    shipment_label: `Shipment #${shipmentId}`,
    unread_count: 1,
    latest_message_preview: preview,
    latest_message_sender_name: senderName,
    latest_message_time_token: String(envelope.timetoken),
    counterparty_company_name: null,
  };
}
